import { createSignal, createEffect, onCleanup, For, Show, untrack } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import Text from '../components/Text';
import Modal from '../components/Modal';
import { FeatureGate } from '../components/FeatureGate';
import { useApi, isOk } from '../api';
import { useActiveBot } from '../state/activeBot';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { subscribeBotEngineEvents } from '../services/botEngineEvents';
import { debounce } from '../services/debounceCallback';
import { formatDateTime } from '../services/dateFormat';
import PlayerListAttachPicker from '../components/PlayerListAttachPicker';
import PlayerNameEntryList, { type PlayerNameEntry } from '../components/PlayerNameEntryList';
import BotLicenseBanner from '../components/BotLicenseBanner';
import { useBotLicense } from '../hooks/useBotLicense';
import { GrantTypes } from '../services/grantsApi';
import type { PlotAclConfigDto, PlotAclActivityDto, PlotAclBanDto, PlotAclWhitelistDto, PlotAclRankExemptionDto, PlotAclPlotDto, PlayerListDto, PlayerListSummaryDto } from '../api';
import { PLOT_ACL_ACTIONS } from '../models/PlotAclAction';
import { GG_RANKS_REGULAR, GG_RANKS_SPECIAL, GG_RANKS_TEAM, ggRankLabel } from '../models/GGRank';
import styles from './PlotAcl.module.css';
import { firstError } from '../api/envelope';

const LOG_LEVELS = ['Debug', 'Info', 'Warning', 'Error'] as const;
const ACTIVITY_PAGE_SIZE = 200;
const ACCESS_MODES = ['Disabled', 'Whitelist', 'Blacklist'] as const;

const CLEAR_RANK_ACTION = 'none';

const ACTION_MESSAGES = [
    { key: 'banListMessage', labelKey: 'PLOTACL_MESSAGE_BAN_LIST' },
    { key: 'rankMessage', labelKey: 'PLOTACL_MESSAGE_RANK' },
    { key: 'accessWhitelistMessage', labelKey: 'PLOTACL_MESSAGE_ACCESS_WHITELIST' },
    { key: 'accessBlacklistMessage', labelKey: 'PLOTACL_MESSAGE_ACCESS_BLACKLIST' },
    { key: 'invisibleMessage', labelKey: 'PLOTACL_MESSAGE_INVISIBLE' },
] as const;

const RANK_GROUPS = [
    { titleKey: 'PLOTACL_RANKS_REGULAR', ranks: GG_RANKS_REGULAR },
    { titleKey: 'PLOTACL_RANKS_SPECIAL', ranks: GG_RANKS_SPECIAL },
    { titleKey: 'PLOTACL_RANKS_TEAM', ranks: GG_RANKS_TEAM },
] as const;

function levelClass(level: string): string {
    switch (level) {
        case 'Error': return styles.badgeDanger;
        case 'Warning': return styles.badgeWarning;
        case 'Info': return styles.badgeInfo;
        default: return styles.badgeMuted;
    }
}

function MemberRow(props: { labelKey: string; names: readonly string[] }) {
    return (
        <div class={styles.memberRow}>
            <span class={styles.memberLabel}><Text val={props.labelKey} /></span>
            <Show when={props.names.length > 0} fallback={
                <span class={styles.memberEmpty}><Text val="PLOTACL_PLOT_NONE" /></span>
            }>
                <For each={props.names}>{(n) => <span class="minicard">{n}</span>}</For>
            </Show>
        </div>
    );
}

export default function PlotAcl() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId } = useActiveBot();
    const playerListEnabled = useFeatureFlag('PLAYER_LIST');
    const { licensed } = useBotLicense(GrantTypes.PlotAclSeat);

    const [config, setConfig] = createSignal<PlotAclConfigDto | null>(null);
    const [online, setOnline] = createSignal(false);
    const [loading, setLoading] = createSignal(true);
    const [busyActive, setBusyActive] = createSignal(false);
    const [savingCfg, setSavingCfg] = createSignal(false);
    const [minLevel, setMinLevel] = createSignal('Info');
    const [activity, setActivity] = createStore<PlotAclActivityDto[]>([]);
    const [bans, setBans] = createStore<PlotAclBanDto[]>([]);
    const [whitelist, setWhitelist] = createStore<PlotAclWhitelistDto[]>([]);
    const [plots, setPlots] = createStore<PlotAclPlotDto[]>([]);
    const [whitelistLists, setWhitelistLists] = createStore<PlayerListSummaryDto[]>([]);
    const [banLists, setBanLists] = createStore<PlayerListSummaryDto[]>([]);
    const [rankExemptions, setRankExemptions] = createStore<PlotAclRankExemptionDto[]>([]);
    const [rankExemptionLists, setRankExemptionLists] = createStore<PlayerListSummaryDto[]>([]);
    const [availableLists, setAvailableLists] = createStore<PlayerListDto[]>([]);
    const [detailAct, setDetailAct] = createSignal<PlotAclActivityDto | null>(null);

    const [form, setForm] = createStore({
        radius: 64, cooldownSeconds: 1, invisibleAction: 'Kick',
        exemptHelpers: true, exemptTrusted: true,
        rankActions: {} as Record<string, string>, unrankedAction: '',
        banListMessage: '', rankMessage: '', accessWhitelistMessage: '',
        accessBlacklistMessage: '', invisibleMessage: '',
    });
    const [formDirty, setFormDirty] = createSignal(false);

    const hydrateForm = (c: PlotAclConfigDto) => {
        setForm({
            radius: c.radius, cooldownSeconds: c.cooldownSeconds,
            invisibleAction: c.invisibleAction,
            exemptHelpers: c.exemptHelpers, exemptTrusted: c.exemptTrusted,
            unrankedAction: c.unrankedAction ?? '',
            banListMessage: c.banListMessage ?? '',
            rankMessage: c.rankMessage ?? '',
            accessWhitelistMessage: c.accessWhitelistMessage ?? '',
            accessBlacklistMessage: c.accessBlacklistMessage ?? '',
            invisibleMessage: c.invisibleMessage ?? '',
        });
        setForm('rankActions', reconcile({ ...c.rankActions }));
    };

    // An empty action means no rule, which the backend expresses as an absent key.
    const setRankAction = (rank: string, action: string) => {
        const next = { ...form.rankActions };
        if (action) next[rank] = action;
        else delete next[rank];
        // reconcile, not a bare object: setting an object on a store path merges, so a removed
        // key would survive.
        setForm('rankActions', reconcile(next));
        setFormDirty(true);
    };

    // The group select needs a value distinct from its own "pick one" placeholder to mean "clear".
    const setGroupRankActions = (ranks: readonly string[], choice: string) => {
        if (!choice) return;
        const next = { ...form.rankActions };
        for (const rank of ranks) {
            if (choice === CLEAR_RANK_ACTION) delete next[rank];
            else next[rank] = choice;
        }
        setForm('rankActions', reconcile(next));
        setFormDirty(true);
    };

    const loadAll = async () => {
        const id = botId();
        if (!id) { setConfig(null); setLoading(false); return; }
        await Promise.all([loadOverview(id), loadActivity(id)]);
        setLoading(false);
    };

    const loadActivity = async (id: string) => {
        const act = await api.plotAcl.getActivity(id, { take: ACTIVITY_PAGE_SIZE, minLevel: minLevel() });
        if (isOk(act.data)) setActivity(reconcile(act.data.value.entries, { key: 'id' }));
    };

    const loadOverview = async (id: string) => {
        const [ov, playerLists] = await Promise.all([
            api.plotAcl.getOverview(id),
            playerListEnabled() ? api.playerList.listPlayerLists() : Promise.resolve(null),
        ]);
        if (isOk(ov.data)) {
            const v = ov.data.value;
            setConfig(v.config ?? null);
            setOnline(v.online);
            setBans(reconcile(v.bans ?? [], { key: 'id' }));
            setWhitelist(reconcile(v.whitelist ?? [], { key: 'id' }));
            setPlots(reconcile(v.plots ?? [], { key: 'id' }));
            setWhitelistLists(reconcile(v.whitelistLists ?? [], { key: 'id' }));
            setBanLists(reconcile(v.banLists ?? [], { key: 'id' }));
            setRankExemptions(reconcile(v.rankExemptions ?? [], { key: 'id' }));
            setRankExemptionLists(reconcile(v.rankExemptionLists ?? [], { key: 'id' }));
            if (v.config && !formDirty()) hydrateForm(v.config);
        }
        if (playerLists && isOk(playerLists.data)) setAvailableLists(reconcile(playerLists.data.value.lists ?? [], { key: 'id' }));
    };

    // The event carries the whole log row, so a busy plot adds rows without a request per entry.
    const prependActivity = (payload: Record<string, any>) => {
        if (LOG_LEVELS.indexOf(payload.level) < LOG_LEVELS.indexOf(minLevel() as typeof LOG_LEVELS[number])) return;
        if (activity.some(entry => entry.id === payload.id)) return;
        const entry: PlotAclActivityDto = {
            id: payload.id,
            level: payload.level,
            type: payload.type,
            message: payload.message,
            reasonCode: payload.reasonCode,
            reasonArgsJson: payload.reasonArgsJson,
            playerName: payload.player,
            playerUuid: payload.playerUuid,
            createdAt: payload.at,
        };
        setActivity(reconcile([entry, ...activity].slice(0, ACTIVITY_PAGE_SIZE), { key: 'id' }));
    };

    // Reload on bot or filter change. Updates in place; the full-page spinner only shows on the
    // first load (loadAll never sets loading back to true), so switching bots doesn't flash.
    createEffect(() => {
        botId();
        minLevel();
        untrack(() => { setFormDirty(false); void loadAll(); });
    });

    // One stable live subscription per bot, independent of the level filter.
    createEffect(() => {
        const id = botId();
        if (!id) return;
        // Kicks, bans and joins change the overview too; refresh it at most every few seconds during a burst.
        const reloadOverview = debounce(() => void loadOverview(id), 1000, 5000);
        const unsub = subscribeBotEngineEvents(id, (evt) => {
            if (evt.type !== 'plugin_event' || evt.payload?.source !== 'plotacl') return;
            if (evt.payload.kind === 'activity' && evt.payload.id) prependActivity(evt.payload);
            reloadOverview();
        });
        onCleanup(() => { reloadOverview.cancel(); unsub(); });
    });

    const toggleActive = async () => {
        const id = botId();
        const cfg = config();
        if (!id) return;
        const target = !(cfg?.isActive ?? false);
        setBusyActive(true);
        const res = await api.plotAcl.setActive(id, { active: target });
        if (isOk(res.data)) {
            toast?.info(t(target ? 'PLOTACL_ENABLE_DISPATCHED' : 'PLOTACL_DISABLE_DISPATCHED'));
            await loadAll();
        } else {
            toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
        }
        setBusyActive(false);
    };

    const saveConfig = async () => {
        const id = botId();
        if (!id) return;
        setSavingCfg(true);
        const res = await api.plotAcl.upsertConfig(id, {
            radius: Number(form.radius) || 64,
            cooldownSeconds: Number(form.cooldownSeconds) || 0,
            invisibleAction: form.invisibleAction,
            exemptHelpers: form.exemptHelpers,
            exemptTrusted: form.exemptTrusted,
            rankActions: form.rankActions,
            unrankedAction: form.unrankedAction || null,
            banListMessage: form.banListMessage || null,
            rankMessage: form.rankMessage || null,
            accessWhitelistMessage: form.accessWhitelistMessage || null,
            accessBlacklistMessage: form.accessBlacklistMessage || null,
            invisibleMessage: form.invisibleMessage || null,
        });
        if (isOk(res.data)) { setConfig(res.data.value); setFormDirty(false); toast?.success(t('PLOTACL_SAVED')); }
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
        setSavingCfg(false);
    };

    const addBan = async (name: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.addBan(id, { playerName: name, playerUuid: null });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const removeBan = async (item: PlayerNameEntry) => {
        const id = botId();
        if (!id) return;
        if (!await showConfirm(t('PLOTACL_CONFIRM_REMOVE_BAN', item.playerName))) return;
        const res = await api.plotAcl.removeBan(id, item.id);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const setBanNote = async (item: PlayerNameEntry, note: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.setBanNote(id, item.id, { note });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const addWhitelist = async (name: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.addWhitelist(id, { playerName: name, playerUuid: null });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const removeWhitelist = async (item: PlayerNameEntry) => {
        const id = botId();
        if (!id) return;
        if (!await showConfirm(t('PLOTACL_CONFIRM_REMOVE_WHITELIST', item.playerName))) return;
        const res = await api.plotAcl.removeWhitelist(id, item.id);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const setWhitelistNote = async (item: PlayerNameEntry, note: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.setWhitelistNote(id, item.id, { note });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const attachWhitelistList = async (playerListId: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.attachWhitelistList(id, playerListId);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const detachWhitelistList = async (playerListId: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.detachWhitelistList(id, playerListId);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const addRankExemption = async (name: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.addRankExemption(id, { playerName: name, playerUuid: null });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const removeRankExemption = async (item: PlayerNameEntry) => {
        const id = botId();
        if (!id) return;
        if (!await showConfirm(t('PLOTACL_CONFIRM_REMOVE_RANK_EXEMPTION', item.playerName))) return;
        const res = await api.plotAcl.removeRankExemption(id, item.id);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const setRankExemptionNote = async (item: PlayerNameEntry, note: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.setRankExemptionNote(id, item.id, { note });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const attachRankExemptionList = async (playerListId: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.attachRankExemptionList(id, playerListId);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const detachRankExemptionList = async (playerListId: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.detachRankExemptionList(id, playerListId);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const attachBanList = async (playerListId: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.attachBanList(id, playerListId);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const detachBanList = async (playerListId: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.detachBanList(id, playerListId);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const attachAccessList = async (plot: PlotAclPlotDto, playerListId: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.attachAccessList(id, plot.id, playerListId);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const detachAccessList = async (plot: PlotAclPlotDto, playerListId: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.detachAccessList(id, plot.id, playerListId);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const savePlotSettings = async (plot: PlotAclPlotDto, accessMode: string, accessAction: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.updatePlotSettings(id, plot.id, { accessMode, accessAction });
        if (isOk(res.data)) { await loadAll(); toast?.success(t('PLOTACL_SAVED')); }
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const addAccess = async (plot: PlotAclPlotDto, name: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.addPlotAccess(id, plot.id, { playerName: name });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const removeAccess = async (plot: PlotAclPlotDto, item: PlayerNameEntry) => {
        const id = botId();
        if (!id) return;
        if (!await showConfirm(t('PLOTACL_CONFIRM_REMOVE_ACCESS', item.playerName))) return;
        const res = await api.plotAcl.removePlotAccess(id, plot.id, item.id);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const setAccessNote = async (plot: PlotAclPlotDto, item: PlayerNameEntry, note: string) => {
        const id = botId();
        if (!id) return;
        const res = await api.plotAcl.setPlotAccessNote(id, plot.id, item.id, { note });
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const removePlot = async (plot: PlotAclPlotDto) => {
        const id = botId();
        if (!id) return;
        if (!await showConfirm(t('PLOTACL_PLOT_CONFIRM_REMOVE', plotLabel(plot)), true)) return;
        const res = await api.plotAcl.removePlot(id, plot.id);
        if (isOk(res.data)) await loadAll();
        else toast?.error(firstError(res.data) ?? t('PLOTACL_TOAST_FAILED'));
    };

    const plotLabel = (plot: PlotAclPlotDto) => `${plot.citybuild} ${plot.plotX};${plot.plotZ}`;

    const actLabel = (type: string) => {
        const key = 'PLOTACL_ACT_' + type;
        const s = t(key);
        return s === key ? type : s;
    };

    const banReasonLabel = (reason: string) => {
        const key = 'PLOTACL_BAN_REASON_' + reason;
        const s = t(key);
        return s === key ? reason : s;
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="PLOTACL_TITLE" /></h1>
            </div>
            <BotLicenseBanner licenseType={GrantTypes.PlotAclSeat} />

            <Show when={!loading()} fallback={<div class={styles.skeletonGrid}><For each={[1, 2, 3, 4, 5, 6]}>{() => <div class={styles.skeletonCard} />}</For></div>}>
                <div class={`fcard ${styles.toggleBar} ${config()?.isActive ? styles.toggleBarOn : styles.toggleBarOff}`}>
                    <div class={styles.toggleStateBlock}>
                        <div class={`${styles.toggleIcon} ${config()?.isActive ? styles.toggleIconOn : styles.toggleIconOff}`}>
                            <i class={`fa-solid ${config()?.isActive ? 'fa-shield-halved' : 'fa-shield'}`} />
                        </div>
                        <div class="v" style={{ gap: '2px' }}>
                            <span class={styles.toggleTitle}>
                                <Text val={config()?.isActive ? 'PLOTACL_ACTIVE' : 'PLOTACL_INACTIVE'} />
                            </span>
                            <span class={styles.toggleHint}>
                                <Text val={config()?.isActive ? 'PLOTACL_ACTIVE_HINT' : 'PLOTACL_INACTIVE_HINT'} />
                                <span style={{ 'margin-left': 'var(--spacing-sm)' }} class={`${styles.badge} ${online() ? styles.badgeSuccess : styles.badgeMuted}`}>
                                    <Text val={online() ? 'PLOTACL_ONLINE' : 'PLOTACL_OFFLINE'} />
                                </span>
                            </span>
                        </div>
                    </div>
                    <button class={`btn ${config()?.isActive ? 'btn-danger' : 'btn-success'}`} disabled={busyActive() || (!config()?.isActive && !licensed())} onClick={() => void toggleActive()}>
                        <i class={`fa-solid ${config()?.isActive ? 'fa-power-off' : 'fa-shield-halved'}`} />
                        <span style={{ 'margin-left': 'var(--spacing-xs)' }}>
                            <Text val={config()?.isActive ? 'PLOTACL_DISABLE' : 'PLOTACL_ENABLE'} />
                        </span>
                    </button>
                </div>

                <div class={styles.grid}>
                    <form class="fcard v" onSubmit={(e) => { e.preventDefault(); void saveConfig(); }} style={{ gap: 'var(--spacing-md)' }}>
                        <h2 class={styles.sectionTitle}><Text val="PLOTACL_SETTINGS" /></h2>

                        <div class={styles.formRow}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="PLOTACL_RADIUS" /></span>
                                <input type="number" min="1" max="256" value={form.radius}
                                    onInput={(e) => { setForm('radius', Number(e.currentTarget.value)); setFormDirty(true); }} />
                            </label>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="PLOTACL_COOLDOWN" /></span>
                                <input type="number" min="0" max="3600" value={form.cooldownSeconds}
                                    onInput={(e) => { setForm('cooldownSeconds', Number(e.currentTarget.value)); setFormDirty(true); }} />
                            </label>
                        </div>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="PLOTACL_INVIS_ACTION" /></span>
                            <select value={form.invisibleAction} onChange={(e) => { setForm('invisibleAction', e.currentTarget.value); setFormDirty(true); }}>
                                <For each={PLOT_ACL_ACTIONS}>{(a) => <option value={a}>{t('PLOTACL_ACTION_' + a)}</option>}</For>
                            </select>
                        </label>

                        <label class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                            <input type="checkbox" checked={form.exemptHelpers} onChange={(e) => { setForm('exemptHelpers', e.currentTarget.checked); setFormDirty(true); }} />
                            <span class={styles.fieldLabel}><Text val="PLOTACL_EXEMPT_HELPERS" /></span>
                        </label>
                        <label class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                            <input type="checkbox" checked={form.exemptTrusted} onChange={(e) => { setForm('exemptTrusted', e.currentTarget.checked); setFormDirty(true); }} />
                            <span class={styles.fieldLabel}><Text val="PLOTACL_EXEMPT_TRUSTED" /></span>
                        </label>

                        <div class={`v ${styles.nestedList}`}>
                            <h3 class={styles.nestedTitle}><Text val="PLOTACL_RANKS" /></h3>
                            <span class={styles.hint}><Text val="PLOTACL_RANKS_HELP" /></span>

                            <For each={RANK_GROUPS}>{(group) => (
                                <div class={`v ${styles.rankGroup}`}>
                                    <div class={`h hcenter ${styles.rankGroupHeader}`}>
                                        <span class={styles.fieldLabel}><Text val={group.titleKey} /></span>
                                        <select value="" onChange={(e) => { setGroupRankActions(group.ranks, e.currentTarget.value); e.currentTarget.value = ''; }}>
                                            <option value="">{t('PLOTACL_RANK_GROUP_APPLY')}</option>
                                            <option value={CLEAR_RANK_ACTION}>{t('PLOTACL_RANK_NO_ACTION')}</option>
                                            <For each={PLOT_ACL_ACTIONS}>{(a) => <option value={a}>{t('PLOTACL_ACTION_' + a)}</option>}</For>
                                        </select>
                                    </div>
                                    <For each={group.ranks}>{(rank) => (
                                        <label class={styles.rankRow}>
                                            <span>{ggRankLabel(rank)}</span>
                                            <select value={form.rankActions[rank] ?? ''} onChange={(e) => setRankAction(rank, e.currentTarget.value)}>
                                                <option value="">{t('PLOTACL_RANK_NO_ACTION')}</option>
                                                <For each={PLOT_ACL_ACTIONS}>{(a) => <option value={a}>{t('PLOTACL_ACTION_' + a)}</option>}</For>
                                            </select>
                                        </label>
                                    )}</For>
                                </div>
                            )}</For>

                            <label class={styles.rankRow}>
                                <span><Text val="PLOTACL_RANK_UNRANKED" /></span>
                                <select value={form.unrankedAction} onChange={(e) => { setForm('unrankedAction', e.currentTarget.value); setFormDirty(true); }}>
                                    <option value="">{t('PLOTACL_RANK_NO_ACTION')}</option>
                                    <For each={PLOT_ACL_ACTIONS}>{(a) => <option value={a}>{t('PLOTACL_ACTION_' + a)}</option>}</For>
                                </select>
                            </label>
                            <span class={styles.hint}><Text val="PLOTACL_RANK_UNRANKED_HELP" /></span>
                        </div>

                        <div class={`v ${styles.nestedList}`}>
                            <h3 class={styles.nestedTitle}><Text val="PLOTACL_MESSAGES" /></h3>
                            <span class={styles.hint}><Text val="PLOTACL_MESSAGES_HELP" /></span>
                            <For each={ACTION_MESSAGES}>{(message) => (
                                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span class={styles.fieldLabel}><Text val={message.labelKey} /></span>
                                    <input type="text" maxLength={256} value={form[message.key]} placeholder={t('PLOTACL_MESSAGE_PLACEHOLDER')}
                                        onInput={(e) => { setForm(message.key, e.currentTarget.value); setFormDirty(true); }} />
                                </label>
                            )}</For>
                        </div>

                        <div class="h" style={{ 'justify-content': 'flex-end' }}>
                            <button type="submit" class="btn btn-primary" disabled={savingCfg()}>
                                <Show when={savingCfg()} fallback={<Text val="SAVE" />}><i class="fa-solid fa-spinner fa-spin" /></Show>
                            </button>
                        </div>
                    </form>

                    <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                        <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                            <h2 class={styles.sectionTitle}><Text val="PLOTACL_BANS" /></h2>
                            <span class={styles.hint}><Text val="PLOTACL_BANS_HELP" /></span>
                            <FeatureGate flag="PLAYER_LIST">
                                <div class={`v ${styles.nestedList}`}>
                                    <h3 class={styles.nestedTitle}><Text val="PLOTACL_BANS_LISTS" /></h3>
                                    <span class={styles.hint}><Text val="PLOTACL_BANS_LISTS_HELP" /></span>
                                    <PlayerListAttachPicker available={availableLists} attached={banLists}
                                        onAttach={attachBanList} onDetach={detachBanList} />
                                </div>
                            </FeatureGate>
                            <PlayerNameEntryList nested
                                titleKey="PLOTACL_BANS_LITERAL" helpKey="PLOTACL_BANS_LITERAL_HELP"
                                placeholderKey="PLOTACL_ADD_BAN_PLACEHOLDER" emptyKey="PLOTACL_NO_BANS"
                                icon="fa-user-shield" items={bans} onAdd={addBan} onRemove={removeBan} onNoteChange={setBanNote}
                                reasonLabel={banReasonLabel} />
                        </div>
                        <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                            <h2 class={styles.sectionTitle}><Text val="PLOTACL_WHITELIST" /></h2>
                            <span class={styles.hint}><Text val="PLOTACL_WHITELIST_HELP" /></span>
                            <FeatureGate flag="PLAYER_LIST">
                                <div class={`v ${styles.nestedList}`}>
                                    <h3 class={styles.nestedTitle}><Text val="PLOTACL_WHITELIST_LISTS" /></h3>
                                    <span class={styles.hint}><Text val="PLOTACL_WHITELIST_LISTS_HELP" /></span>
                                    <PlayerListAttachPicker available={availableLists} attached={whitelistLists}
                                        onAttach={attachWhitelistList} onDetach={detachWhitelistList} />
                                </div>
                            </FeatureGate>
                            <PlayerNameEntryList nested
                                titleKey="PLOTACL_WHITELIST_LITERAL" helpKey="PLOTACL_WHITELIST_LITERAL_HELP"
                                placeholderKey="PLOTACL_ADD_WHITELIST_PLACEHOLDER" emptyKey="PLOTACL_NO_WHITELIST"
                                icon="fa-user-check" items={whitelist} onAdd={addWhitelist} onRemove={removeWhitelist} onNoteChange={setWhitelistNote} />
                        </div>
                        <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                            <h2 class={styles.sectionTitle}><Text val="PLOTACL_RANK_EXEMPTIONS" /></h2>
                            <span class={styles.hint}><Text val="PLOTACL_RANK_EXEMPTIONS_HELP" /></span>
                            <FeatureGate flag="PLAYER_LIST">
                                <div class={`v ${styles.nestedList}`}>
                                    <h3 class={styles.nestedTitle}><Text val="PLOTACL_RANK_EXEMPTIONS_LISTS" /></h3>
                                    <span class={styles.hint}><Text val="PLOTACL_RANK_EXEMPTIONS_LISTS_HELP" /></span>
                                    <PlayerListAttachPicker available={availableLists} attached={rankExemptionLists}
                                        onAttach={attachRankExemptionList} onDetach={detachRankExemptionList} />
                                </div>
                            </FeatureGate>
                            <PlayerNameEntryList nested
                                titleKey="PLOTACL_RANK_EXEMPTIONS_LITERAL" helpKey="PLOTACL_RANK_EXEMPTIONS_LITERAL_HELP"
                                placeholderKey="PLOTACL_ADD_WHITELIST_PLACEHOLDER" emptyKey="PLOTACL_NO_RANK_EXEMPTIONS"
                                icon="fa-user-tag" items={rankExemptions} onAdd={addRankExemption} onRemove={removeRankExemption}
                                onNoteChange={setRankExemptionNote} />
                        </div>
                    </div>
                </div>

                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                    <h2 class={styles.sectionTitle}><Text val="PLOTACL_PLOTS" /></h2>
                    <span class={styles.hint}><Text val="PLOTACL_PLOTS_HELP" /></span>
                    <Show when={plots.length > 0} fallback={
                        <div class={styles.emptyPlots}>
                            <i class="fa-solid fa-map-location-dot" />
                            <p><Text val="PLOTACL_NO_PLOTS" /></p>
                        </div>
                    }>
                        <div class={styles.plotList}>
                            <For each={plots}>{(plot) => (
                                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                                    <div class={styles.plotHeader}>
                                        <span class={`${styles.badge} ${styles.badgeInfo}`}>{plot.citybuild}</span>
                                        <span class={styles.plotId}>{plot.plotX};{plot.plotZ}</span>
                                        <Show when={plot.ownerName}>
                                            <span class={styles.owner}><Text val="PLOTACL_PLOT_OWNER" />: {plot.ownerName}</span>
                                        </Show>
                                        <span class={styles.plotHeaderSpacer} />
                                        <Show when={plot.lastInfoAt}>
                                            <span class={styles.txnTime}>{t('PLOTACL_PLOT_LAST_READ', formatDateTime(plot.lastInfoAt!))}</span>
                                        </Show>
                                        <button class="btn btn-icon btn-danger btn-sm" title={t('PLOTACL_PLOT_REMOVE')}
                                            onClick={() => void removePlot(plot)}>
                                            <i class="fa-solid fa-trash" />
                                        </button>
                                    </div>

                                    <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                        <MemberRow labelKey="PLOTACL_PLOT_HELPERS" names={plot.helpers} />
                                        <MemberRow labelKey="PLOTACL_PLOT_TRUSTED" names={plot.trusted} />
                                        <MemberRow labelKey="PLOTACL_PLOT_DENIED" names={plot.denied} />
                                        <span class={styles.hint}><Text val="PLOTACL_PLOT_REFRESH_HINT" /></span>
                                    </div>

                                    <div class={styles.formRow}>
                                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                            <span class={styles.fieldLabel}><Text val="PLOTACL_ACCESS_MODE" /></span>
                                            <select value={plot.accessMode}
                                                onChange={(e) => void savePlotSettings(plot, e.currentTarget.value, plot.accessAction)}>
                                                <For each={ACCESS_MODES}>{(m) => <option value={m}>{t('PLOTACL_ACCESS_MODE_' + m)}</option>}</For>
                                            </select>
                                            <span class={styles.hint}>{t('PLOTACL_ACCESS_MODE_HELP_' + plot.accessMode)}</span>
                                        </label>
                                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                            <span class={styles.fieldLabel}><Text val="PLOTACL_ACCESS_ACTION" /></span>
                                            <select value={plot.accessAction} disabled={plot.accessMode === 'Disabled'}
                                                onChange={(e) => void savePlotSettings(plot, plot.accessMode, e.currentTarget.value)}>
                                                <For each={PLOT_ACL_ACTIONS}>{(a) => <option value={a}>{t('PLOTACL_ACTION_' + a)}</option>}</For>
                                            </select>
                                            <span class={styles.hint}><Text val="PLOTACL_ACCESS_ACTION_HELP" /></span>
                                        </label>
                                    </div>

                                    <Show when={plot.accessMode === 'Whitelist' && plot.access.length === 0}>
                                        <div class={styles.warning}>
                                            <i class="fa-solid fa-triangle-exclamation" />
                                            <span><Text val="PLOTACL_ACCESS_EMPTY_WHITELIST_WARNING" /></span>
                                        </div>
                                    </Show>

                                    <FeatureGate flag="PLAYER_LIST">
                                        <div class={`v ${styles.nestedList}`}>
                                            <h3 class={styles.nestedTitle}><Text val="PLOTACL_ACCESS_LISTS" /></h3>
                                            <span class={styles.hint}><Text val="PLOTACL_ACCESS_LISTS_HELP" /></span>
                                            <PlayerListAttachPicker available={availableLists} attached={plot.attachedLists}
                                                onAttach={(id) => attachAccessList(plot, id)}
                                                onDetach={(id) => detachAccessList(plot, id)} />
                                        </div>
                                    </FeatureGate>

                                    <PlayerNameEntryList nested
                                        titleKey="PLOTACL_ACCESS_LIST" helpKey="PLOTACL_ACCESS_LIST_HELP"
                                        placeholderKey="PLOTACL_ADD_ACCESS_PLACEHOLDER" emptyKey="PLOTACL_NO_ACCESS"
                                        icon="fa-user-lock" items={plot.access}
                                        onAdd={(name) => addAccess(plot, name)}
                                        onRemove={(item) => removeAccess(plot, item)}
                                        onNoteChange={(item, note) => setAccessNote(plot, item, note)} />
                                </div>
                            )}</For>
                        </div>
                    </Show>
                </div>

                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                    <div class="h hcenter" style={{ 'justify-content': 'space-between' }}>
                        <h2 class={styles.sectionTitle}><Text val="PLOTACL_ACTIVITY" /></h2>
                        <select value={minLevel()} onChange={(e) => setMinLevel(e.currentTarget.value)}>
                            <For each={LOG_LEVELS}>{(l) => <option value={l}>{t('PLOTACL_LOG_' + l.toUpperCase())}</option>}</For>
                        </select>
                    </div>
                    <Show when={activity.length > 0} fallback={
                        <div class={styles.emptySmall}><i class="fa-solid fa-stream" /><p><Text val="PLOTACL_NO_ACTIVITY" /></p></div>
                    }>
                        <div class={styles.feed}>
                            <For each={activity}>{(a) => (
                                <div class={`${styles.actRow} ${styles.clickable}`} role="button" tabindex="0"
                                    onClick={() => setDetailAct(a)}>
                                    <span class={`${styles.badge} ${levelClass(a.level)}`}>{t('PLOTACL_LOG_' + a.level.toUpperCase())}</span>
                                    <span class={styles.actType}>{actLabel(a.type)}{a.playerName ? ` · ${a.playerName}` : ''}</span>
                                    <span class={styles.txnTime}>{formatDateTime(a.createdAt)}</span>
                                </div>
                            )}</For>
                        </div>
                    </Show>
                </div>
            </Show>

            <Modal open={detailAct() != null} onClose={() => setDetailAct(null)} title={t('PLOTACL_DETAIL_TITLE')}>
                <Show when={detailAct()}>
                    {(a) => (
                        <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                            <div><b><Text val="PLOTACL_DETAIL_TYPE" />:</b> {actLabel(a().type)}</div>
                            <div><b><Text val="PLOTACL_DETAIL_LEVEL" />:</b> {t('PLOTACL_LOG_' + a().level.toUpperCase())}</div>
                            <Show when={a().playerName}><div><b><Text val="PLOTACL_DETAIL_PLAYER" />:</b> {a().playerName}</div></Show>
                            <Show when={a().reasonCode}><div><b><Text val="PLOTACL_DETAIL_REASON" />:</b> {a().reasonCode}</div></Show>
                            <Show when={a().message}><div>{a().message}</div></Show>
                            <Show when={a().reasonArgsJson}><pre class={styles.args}>{a().reasonArgsJson}</pre></Show>
                            <div class={styles.txnTime}>{formatDateTime(a().createdAt)}</div>
                        </div>
                    )}
                </Show>
            </Modal>
        </div>
    );
}
